import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { partnerApi, bpsToPct, TOS_VERSION_CURRENT } from "@/lib/partner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleButton, AuthDivider } from "@/components/GoogleButton";
import { AppleButton } from "@/components/AppleButton";
import COUNTRY_CODES, { detectBrowserCountryIso } from "@/lib/countryCodes";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Copy, ExternalLink, Loader2, Sparkles,
  User, Video, Building2,
} from "lucide-react";
import { useLocale } from "@/i18n/useLocale";
import content from "@/data/partner_public_content.json";

// ---- static config (fallback if /partner/program/config is unreachable) -----
const FALLBACK_CONFIG = {
  tos_version: TOS_VERSION_CURRENT,
  attribution_window_days: 90,
  min_payout_cents: 5000,
  min_payout_currency: "USD",
  tiers: [
    { label: "Starter", max_seats: 20, rate_bps: 1500, rate_pct: 15 },
    { label: "Growth", max_seats: 40, rate_bps: 2000, rate_pct: 20 },
    { label: "Scale", max_seats: 100, rate_bps: 2500, rate_pct: 25 },
    { label: "Elite", max_seats: null, rate_bps: 3000, rate_pct: 30 },
  ],
};

const DRAFT_KEY = "tp_partner_apply_draft";
const RETURN_TO = "/partners/apply";

const TYPE_IDS = ["individual", "creator", "agency"];
const TYPE_ICONS = { individual: User, creator: Video, agency: Building2 };

const AUDIENCE_SIZES = ["<1k", "1k–10k", "10k–100k", "100k–1M", "1M+"];
const PRIMARY_PLATFORMS = ["YouTube", "Instagram", "TikTok", "X", "LinkedIn", "Podcast", "Newsletter", "Other"];
const TEAM_SIZES = ["Solo", "2–5", "6–20", "21–50", "50+"];
const CLIENT_COUNTS = ["<5", "5–20", "20–100", "100+"];
const PROMO_CHANNEL_KEYS = ["word_of_mouth", "personal_social", "email", "other"];

// Public program config — no auth, so we call the raw /api URL directly.
const publicApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL || ""}/api` });

const emptyProfile = () => ({
  full_name: "", country: detectBrowserCountryIso().iso || "", payout_email: "",
  promo_channels: [],
  audience_size: "", primary_platform: "", primary_platforms: [],
  content_niche: "", website_or_handle: "",
  company_name: "", company_website: "", team_size: "", client_count_estimate: "", services_offered: "",
});

const loadDraft = () => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
};
const saveDraft = (partial) => {
  try { localStorage.setItem(DRAFT_KEY, JSON.stringify(partial)); } catch { /* ignore */ }
};
const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } };

// Small string interpolation helper — supports {name} placeholders in content strings.
const fmt = (tpl, vars) =>
  String(tpl || "").replace(/\{(\w+)\}/g, (_, k) => (vars[k] === undefined || vars[k] === null ? "" : String(vars[k])));

export default function PartnerApplyWizard() {
  const nav = useNavigate();
  const { user, ready, applyData } = useAuth() || {};
  const { locale, isRtl } = useLocale();
  const isAr = String(locale || "en").toLowerCase().startsWith("ar");
  const t = (isAr ? content.ar : content.en).apply;

  const [config, setConfig] = useState(FALLBACK_CONFIG);
  const [bootLoading, setBootLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [profile, setProfile] = useState(emptyProfile);
  const [tos, setTos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [alreadyPartner, setAlreadyPartner] = useState(null);

  const totalSteps = 5;
  const isStepValid = useMemo(() => {
    if (step === 1) return !!type;
    if (step === 2) return !!user;
    if (step === 3) {
      if (!profile.full_name?.trim() || !profile.country || !profile.payout_email?.trim()) return false;
      if (type === "individual") return (profile.promo_channels || []).length > 0;
      if (type === "creator") return !!(profile.audience_size);
      if (type === "agency") return !!(profile.company_name?.trim() && profile.team_size);
      return true;
    }
    if (step === 4) return !!tos;
    return true;
  }, [step, type, profile, tos, user]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await publicApi.get("/partner/program/config").then((r) => r.data);
        if (!cancelled && cfg) setConfig(cfg);
      } catch { /* fallback to FALLBACK_CONFIG */ }

      const draft = loadDraft();
      if (draft && !cancelled) {
        if (draft.type) setType(draft.type);
        if (draft.profile) setProfile((p) => ({ ...p, ...draft.profile }));
        if (typeof draft.step === "number" && draft.step >= 2 && draft.step <= 4) {
          setStep(draft.step);
        } else if (draft.step === 5) {
          clearDraft();
        }
      }

      if (!ready) return;

      if (user) {
        setProfile((p) => (p.full_name ? p : { ...p, full_name: user.name || "" }));
        try {
          const me = await partnerApi.me();
          if (cancelled) return;
          const st = me?.partner?.status;
          if (st === "active") {
            clearDraft();
            nav("/partner/dashboard", { replace: true });
            return;
          }
          if (st) {
            setAlreadyPartner({ status: st, partner_code: me?.partner?.partner_code });
            setStep(1);
          }
        } catch {
          // 404 not_a_partner → normal apply path.
        }
        setStep((s) => (s === 2 ? 3 : s));
      }

      if (!cancelled) setBootLoading(false);
    })();
    return () => { cancelled = true; };
  }, [ready, user, nav]);

  useEffect(() => {
    if (bootLoading) return;
    if (step >= 5) return;
    saveDraft({ step, type, profile });
  }, [step, type, profile, bootLoading]);

  useEffect(() => {
    if (!bootLoading && step === 5 && !result) {
      clearDraft();
      setStep(1);
    }
  }, [bootLoading, step, result]);

  const next = () => {
    if (!isStepValid) return;
    if (step === 1 && !user) return setStep(2);
    if (step === 2 && user) return setStep(3);
    setStep((s) => Math.min(totalSteps, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!tos) return toast.error(t.submitTos);
    if (!user) return setStep(2);
    setSubmitting(true);
    try {
      const payload = {
        tos_accepted: true,
        tos_version: config.tos_version || TOS_VERSION_CURRENT,
        partner_type: type,
        payout_method: "manual_bank",
        payout_email: profile.payout_email.trim() || undefined,
        country: profile.country || undefined,
        application_source: "partners_apply_page",
      };
      if (type === "individual") {
        payload.promo_channels = profile.promo_channels;
      } else if (type === "creator") {
        payload.audience_size = profile.audience_size;
        const plats = Array.isArray(profile.primary_platforms) ? profile.primary_platforms : [];
        if (plats.length > 0) {
          payload.primary_platforms = plats;
        } else if (profile.primary_platform) {
          payload.primary_platform = profile.primary_platform;
        }
        payload.content_niche = profile.content_niche?.trim() || undefined;
        payload.website_or_handle = profile.website_or_handle?.trim() || undefined;
      } else if (type === "agency") {
        payload.company_name = profile.company_name?.trim();
        payload.company_website = profile.company_website?.trim() || undefined;
        payload.team_size = profile.team_size;
        payload.client_count_estimate = profile.client_count_estimate || undefined;
        payload.services_offered = profile.services_offered?.trim() || undefined;
      }
      const res = await partnerApi.apply(payload);
      clearDraft();
      setResult(res);
      setStep(5);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.submitFailed);
    } finally { setSubmitting(false); }
  };

  if (!ready || bootLoading) {
    return (
      <div className="mx-auto flex min-h-screen max-w-2xl items-center justify-center px-5 text-white/60" data-testid="partners-apply-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const stepLabel = t.progress.stepLabels[String(step)];
  const arrowSide = isRtl ? "rotate-180" : "";

  return (
    <div
      dir={isRtl ? "rtl" : "ltr"}
      className="aria-dark relative min-h-screen w-full max-w-full overflow-x-hidden bg-[#050607] text-white"
      style={{ fontFamily: "'Outfit', sans-serif" }}
      data-testid="partners-apply-page"
    >
      <div className="grain-overlay pointer-events-none" style={{ opacity: 0.05 }} />
      <div className="aria-gold-radial pointer-events-none absolute inset-0 opacity-60" />

      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#050607]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[60px] w-full min-w-0 max-w-[1080px] items-center justify-between px-5 sm:px-8">
          <Link to="/partners" className="flex items-center gap-2.5" data-testid="apply-brand">
            <img src="/tp-mark.png" alt="TapPresence" className="h-7 w-7 object-contain" />
            <span className="text-[14px] font-semibold tracking-tight">{t.header.brand}</span>
          </Link>
          <Link to="/partners" className="text-xs text-white/50 hover:text-white" data-testid="apply-exit">
            {t.header.backToProgram}
          </Link>
        </div>
      </header>

      {/* Progress */}
      <div className="mx-auto w-full min-w-0 max-w-[1080px] px-5 pt-8 sm:px-8">
        <div className="flex items-center justify-between" data-testid="apply-progress">
          <p className="text-[11px] uppercase tracking-wider text-white/40">
            {fmt(t.progress.stepOf, { n: step, total: totalSteps })}
          </p>
          <p className="text-[11px] text-[#D6A653]/80">{stepLabel}</p>
        </div>
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-white/8">
          <div
            className="h-full bg-[#D6A653] transition-all duration-500"
            style={{ width: `${(step / totalSteps) * 100}%` }}
            data-testid="apply-progress-bar"
          />
        </div>
      </div>

      {alreadyPartner && step !== 5 ? (
        <ExistingPartnerBanner t={t} status={alreadyPartner.status} onDashboard={() => nav("/partner/dashboard")} />
      ) : null}

      <main className="mx-auto w-full min-w-0 max-w-[1080px] px-5 pb-24 pt-8 sm:px-8">
        {step === 1 && <StepType t={t} selected={type} onPick={(id) => { setType(id); setStep(user ? 3 : 2); }} />}
        {step === 1 && !user && !alreadyPartner && (
          <div className="mt-8 text-sm text-white/60" data-testid="apply-step1-signin-row">
            {t.stepType.alreadyPartnerPrompt}{" "}
            <Link
              to="/login?return_to=/partner/dashboard&intent=partner"
              className="text-[#D6A653] underline underline-offset-4 hover:text-[#F0CD84]"
              data-testid="apply-step1-signin-link"
            >
              {t.stepType.signInLink}
            </Link>
          </div>
        )}
        {step === 2 && <StepAuth t={t} user={user} onSignedUp={(sessionData) => { applyData?.(sessionData); setStep(3); }} />}
        {step === 3 && <StepProfile t={t} type={type} profile={profile} setProfile={setProfile} />}
        {step === 4 && <StepAgree t={t} config={config} type={type} profile={profile} tos={tos} setTos={setTos} />}
        {step === 5 && <StepSuccess t={t} result={result} onDashboard={() => nav("/partner/dashboard")} />}

        {step !== 5 && (
          <div className="mt-10 flex flex-wrap items-center justify-between gap-3" data-testid="apply-nav">
            {step > 1 ? (
              <Button
                variant="ghost"
                onClick={back}
                data-testid="apply-back"
                className="rounded-full border border-white/12 bg-transparent px-5 py-3 text-sm text-white/70 hover:bg-white/5"
              >
                <ArrowLeft className={`mr-1.5 h-4 w-4 ${arrowSide}`} /> {t.nav.back}
              </Button>
            ) : <span />}
            {step === 4 ? (
              <Button
                onClick={submit}
                disabled={submitting || !isStepValid}
                data-testid="apply-submit"
                className="rounded-full bg-[#D6A653] px-6 py-3 text-sm font-medium text-black hover:bg-[#c99a49] disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t.nav.submit}
              </Button>
            ) : step === 2 ? null : (
              <Button
                onClick={next}
                disabled={!isStepValid}
                data-testid="apply-next"
                className="rounded-full bg-[#D6A653] px-6 py-3 text-sm font-medium text-black hover:bg-[#c99a49] disabled:opacity-50"
              >
                {t.nav.continue} <ArrowRight className={`ml-1.5 h-4 w-4 ${arrowSide}`} />
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

const ExistingPartnerBanner = ({ t, status, onDashboard }) => {
  const line = status === "pending" ? t.existingBanner.underReview : t.existingBanner.onFile;
  return (
    <div className="mx-auto mt-6 w-full min-w-0 max-w-[1080px] px-5 sm:px-8" data-testid="apply-existing-banner">
      <div className="rounded-2xl border border-[#D6A653]/30 bg-[#D6A653]/8 p-4 text-sm">
        <p className="text-[#F0CD84]">
          {line} {t.existingBanner.editHint}
        </p>
        <button
          onClick={onDashboard}
          data-testid="apply-existing-dashboard"
          className="mt-2 text-xs text-[#D6A653] underline underline-offset-2"
        >
          {t.existingBanner.gotoDashboard}
        </button>
      </div>
    </div>
  );
};

const StepType = ({ t, selected, onPick }) => (
  <div data-testid="apply-step-type">
    <div className="inline-flex items-center gap-2 rounded-full border border-[#D6A653]/30 bg-[#0D1014] px-3 py-1">
      <Sparkles className="h-3 w-3 text-[#F0CD84]" />
      <span className="text-[11px] text-[#E6C787]">{t.stepType.badge}</span>
    </div>
    <h1 className="mt-5 text-3xl font-semibold tracking-tight sm:text-4xl">
      {t.stepType.titlePrefix}<span className="lp-shine">{t.stepType.titleProduct}</span>{t.stepType.titleSuffix}
    </h1>
    <p className="mt-3 max-w-[560px] text-sm leading-relaxed text-white/60">
      {t.stepType.sub}
    </p>
    <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3" data-testid="apply-type-grid">
      {TYPE_IDS.map((id) => {
        const active = selected === id;
        const Icon = TYPE_ICONS[id];
        const tt = t.stepType.types[id];
        return (
          <button
            key={id}
            type="button"
            onClick={() => onPick(id)}
            data-testid={`apply-type-${id}`}
            className={`group relative w-full min-w-0 rounded-2xl border p-5 text-left transition-all ${
              active
                ? "border-[#D6A653] bg-[#D6A653]/8 shadow-[0_0_0_1px_rgba(214,166,83,0.4)]"
                : "border-white/10 bg-[#0B0D12] hover:border-white/25 hover:bg-[#101319]"
            }`}
          >
            <Icon className={`h-6 w-6 ${active ? "text-[#D6A653]" : "text-white/50 group-hover:text-white"}`} />
            <p className="mt-4 text-lg font-medium">{tt.title}</p>
            <p className="mt-1 text-xs text-[#D6A653]/80">{tt.tagline}</p>
            <p className="mt-3 text-xs leading-relaxed text-white/55">{tt.body}</p>
            {active ? (
              <span className="absolute right-4 top-4 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#D6A653] text-[10px] text-black">
                <CheckCircle2 className="h-3.5 w-3.5" />
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  </div>
);

const StepAuth = ({ t, user, onSignedUp }) => {
  const [form, setForm] = useState({ full_name: "", email: "", password: "", marketing_consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [emailExists, setEmailExists] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  const canSubmit = form.full_name.trim() && form.email.trim() && form.password.length >= 8;

  const submit = async (ev) => {
    ev.preventDefault();
    setError("");
    setEmailExists(false);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const data = await partnerApi.signup({
        email: form.email.trim(),
        password: form.password,
        full_name: form.full_name.trim(),
        marketing_consent: !!form.marketing_consent,
      });
      onSignedUp?.(data);
      toast.success(t.stepAuth.cardA.successToast);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 409 && detail === "email_exists") {
        setEmailExists(true);
        setError(t.stepAuth.cardA.errors.emailExists);
      } else if (status === 429) {
        setError(t.stepAuth.cardA.errors.rateLimited);
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(t.stepAuth.cardA.errors.generic);
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="apply-step-auth">
      <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{t.stepAuth.title}</h1>
      <p className="mt-3 max-w-[600px] text-sm leading-relaxed text-white/60">
        {t.stepAuth.sub}
      </p>

      {user ? (
        <div className="mt-8 rounded-2xl border border-[#D6A653]/30 bg-[#D6A653]/8 p-5" data-testid="apply-step-auth-loggedin">
          <p className="text-sm text-[#F0CD84]">
            {t.stepAuth.signedInAs} <span className="font-medium">{user.email}</span>.
          </p>
        </div>
      ) : (
        <div className="mt-8 grid gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
          <div className="rounded-2xl border border-[#D6A653]/40 bg-[#D6A653]/6 p-6" data-testid="apply-signup-card">
            <p className="text-xs font-medium uppercase tracking-wider text-[#D6A653]">{t.stepAuth.cardA.recommendedBadge}</p>
            <h2 className="mt-2 text-lg font-medium">{t.stepAuth.cardA.title}</h2>

            <div className="mt-4 space-y-3">
              <GoogleButton label={t.stepAuth.cardA.google} intent="partner_signup" testId="apply-google-signup" />
              <AppleButton label={t.stepAuth.cardA.apple} intent="partner_signup" testId="apply-apple-signup" />
            </div>

            <AuthDivider />

            <form onSubmit={submit} className="space-y-3" data-testid="apply-signup-form">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-white/50">{t.stepAuth.cardA.fullName}</Label>
                <Input value={form.full_name} onChange={set("full_name")} data-testid="apply-signup-name" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-white/50">{t.stepAuth.cardA.email}</Label>
                <Input type="email" value={form.email} onChange={set("email")} data-testid="apply-signup-email" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-white/50">{t.stepAuth.cardA.password}</Label>
                <Input type="password" value={form.password} onChange={set("password")} minLength={8} placeholder={t.stepAuth.cardA.passwordPlaceholder} data-testid="apply-signup-password" required />
              </div>
              <label className="flex items-start gap-2 pt-1 text-xs text-white/60">
                <input
                  type="checkbox"
                  checked={form.marketing_consent}
                  onChange={set("marketing_consent")}
                  className="mt-0.5 h-3.5 w-3.5 accent-[#D6A653]"
                  data-testid="apply-signup-marketing"
                />
                <span>{t.stepAuth.cardA.marketingConsent}</span>
              </label>
              {error ? (
                <div
                  className="rounded-md border border-red-500/30 bg-red-500/8 px-3 py-2 text-xs text-red-200"
                  data-testid="apply-signup-error"
                >
                  <p>{error}</p>
                  {emailExists ? (
                    <Link
                      to={`/login?return_to=${encodeURIComponent(RETURN_TO)}&intent=partner`}
                      data-testid="apply-signup-error-login"
                      className="mt-2 inline-flex items-center rounded-full bg-[#D6A653] px-3 py-1 text-[11px] font-medium text-black transition-colors hover:bg-[#c99a49]"
                    >
                      {t.stepAuth.cardA.errors.loginToContinue} <ArrowRight className="ml-1 h-3 w-3" />
                    </Link>
                  ) : null}
                </div>
              ) : null}
              <Button
                type="submit"
                disabled={!canSubmit || submitting}
                data-testid="apply-signup-submit"
                className="w-full rounded-full bg-[#D6A653] py-3 text-sm font-medium text-black hover:bg-[#c99a49] disabled:opacity-50"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t.stepAuth.cardA.submit}
              </Button>
              <p className="pt-1 text-[11px] leading-relaxed text-white/40">
                {t.stepAuth.cardA.freeForever}
              </p>
            </form>
          </div>

          <Link
            to={`/login?return_to=${encodeURIComponent(RETURN_TO)}&intent=partner`}
            data-testid="apply-auth-login"
            className="group flex h-full flex-col justify-between rounded-2xl border border-white/12 bg-[#0B0D12] p-6 transition-all hover:border-white/25"
          >
            <div>
              <User className="h-5 w-5 text-white/60" />
              <h2 className="mt-4 text-lg font-medium">{t.stepAuth.cardB.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-white/55">
                {t.stepAuth.cardB.body}
              </p>
            </div>
            <p className="mt-6 inline-flex items-center text-sm text-[#D6A653] group-hover:underline">
              {t.stepAuth.cardB.cta} <ArrowRight className="ml-1.5 h-4 w-4" />
            </p>
          </Link>
        </div>
      )}
    </div>
  );
};

const StepProfile = ({ t, type, profile, setProfile }) => {
  const set = (k) => (e) => setProfile((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));
  const toggleChannel = (k) => setProfile((p) => {
    const has = p.promo_channels.includes(k);
    return { ...p, promo_channels: has ? p.promo_channels.filter((x) => x !== k) : [...p.promo_channels, k] };
  });

  return (
    <div data-testid="apply-step-profile">
      <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{t.stepProfile.title}</h1>
      <p className="mt-3 max-w-[540px] text-sm leading-relaxed text-white/60">
        {t.stepProfile.sub}
      </p>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t.stepProfile.fullName}>
          <Input value={profile.full_name} onChange={set("full_name")} data-testid="apply-profile-name" />
        </Field>
        <Field label={t.stepProfile.country}>
          <select
            value={profile.country}
            onChange={(e) => setProfile((p) => ({ ...p, country: e.target.value }))}
            data-testid="apply-profile-country"
            className="h-10 w-full rounded-md border border-white/15 bg-[#0A0B0D] px-3 text-sm text-white outline-none focus:border-[#D6A653]/60"
          >
            <option value="" disabled>{t.stepProfile.selectCountry}</option>
            {COUNTRY_CODES.map((c) => (
              <option key={c.iso} value={c.iso}>
                {c.flag} {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t.stepProfile.payoutEmail} hint={t.stepProfile.payoutEmailHint}>
          <Input
            type="email"
            value={profile.payout_email}
            onChange={set("payout_email")}
            placeholder={t.stepProfile.payoutEmailPlaceholder}
            data-testid="apply-profile-payout-email"
          />
        </Field>
      </div>

      {type === "individual" && (
        <div className="mt-8" data-testid="apply-profile-individual">
          <Label className="text-xs uppercase tracking-wider text-white/45">{t.stepProfile.howPromote}</Label>
          <div className="mt-3 flex flex-wrap gap-2">
            {PROMO_CHANNEL_KEYS.map((k) => {
              const active = profile.promo_channels.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleChannel(k)}
                  data-testid={`apply-channel-${k}`}
                  className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                    active
                      ? "border-[#D6A653] bg-[#D6A653]/15 text-[#D6A653]"
                      : "border-white/15 text-white/60 hover:text-white"
                  }`}
                >
                  {t.stepProfile.channels[k]}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {type === "creator" && (
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="apply-profile-creator">
          <Field label={t.stepProfile.audienceSize}>
            <Chips
              options={AUDIENCE_SIZES.map((s) => ({ k: s, label: s }))}
              value={profile.audience_size}
              onChange={(v) => setProfile((p) => ({ ...p, audience_size: v }))}
              testIdPrefix="apply-audience"
            />
          </Field>
          <Field label={t.stepProfile.primaryPlatforms} hint={t.stepProfile.primaryPlatformsHint}>
            <div className="flex flex-wrap gap-2" data-testid="apply-primary-platforms">
              {PRIMARY_PLATFORMS.map((p) => {
                const active = (profile.primary_platforms || []).includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setProfile((s) => {
                      const cur = Array.isArray(s.primary_platforms) ? s.primary_platforms : [];
                      const next = cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p];
                      return { ...s, primary_platforms: next };
                    })}
                    data-testid={`apply-primary-platform-${p}`}
                    className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                      active
                        ? "border-[#D6A653] bg-[#D6A653]/15 text-[#D6A653]"
                        : "border-white/15 text-white/60 hover:text-white"
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label={t.stepProfile.contentNiche} hint={t.stepProfile.contentNicheHint}>
            <Input value={profile.content_niche} onChange={set("content_niche")} data-testid="apply-content-niche" />
          </Field>
          <Field label={t.stepProfile.websiteOrHandle} hint={t.stepProfile.websiteOrHandleHint}>
            <Input value={profile.website_or_handle} onChange={set("website_or_handle")} placeholder={t.stepProfile.websiteOrHandlePlaceholder} data-testid="apply-website-handle" />
          </Field>
        </div>
      )}

      {type === "agency" && (
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="apply-profile-agency">
          <Field label={t.stepProfile.companyName}>
            <Input value={profile.company_name} onChange={set("company_name")} data-testid="apply-company-name" />
          </Field>
          <Field label={t.stepProfile.companyWebsite}>
            <Input value={profile.company_website} onChange={set("company_website")} placeholder={t.stepProfile.companyWebsitePlaceholder} data-testid="apply-company-website" />
          </Field>
          <Field label={t.stepProfile.teamSize}>
            <Chips
              options={TEAM_SIZES.map((s) => ({ k: s, label: s }))}
              value={profile.team_size}
              onChange={(v) => setProfile((p) => ({ ...p, team_size: v }))}
              testIdPrefix="apply-team-size"
            />
          </Field>
          <Field label={t.stepProfile.clientCount}>
            <Chips
              options={CLIENT_COUNTS.map((s) => ({ k: s, label: s }))}
              value={profile.client_count_estimate}
              onChange={(v) => setProfile((p) => ({ ...p, client_count_estimate: v }))}
              testIdPrefix="apply-client-count"
            />
          </Field>
          <Field label={t.stepProfile.servicesOffered} hint={t.stepProfile.servicesOfferedHint}>
            <Input value={profile.services_offered} onChange={set("services_offered")} data-testid="apply-services-offered" />
          </Field>
        </div>
      )}
    </div>
  );
};

const StepAgree = ({ t, config, type, profile, tos, setTos }) => {
  const tiers = config.tiers || [];
  const attribText = fmt(t.stepAgree.attribution, {
    days: config.attribution_window_days,
    amount: (config.min_payout_cents / 100).toFixed(0),
    currency: config.min_payout_currency,
  });
  return (
    <div data-testid="apply-step-agree">
      <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{t.stepAgree.title}</h1>
      <p className="mt-3 max-w-[540px] text-sm leading-relaxed text-white/60">
        {t.stepAgree.sub}
      </p>

      <div className="mt-8 rounded-2xl border border-white/10 bg-[#0B0D12] p-5" data-testid="apply-agree-summary">
        <SummaryRow label={t.stepAgree.summary.type} value={<span className="capitalize">{type}</span>} />
        <SummaryRow label={t.stepAgree.summary.fullName} value={profile.full_name} />
        <SummaryRow label={t.stepAgree.summary.country} value={profile.country} />
        <SummaryRow label={t.stepAgree.summary.payoutEmail} value={profile.payout_email} />
      </div>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#0B0D12] p-5" data-testid="apply-agree-tiers">
        <p className="text-[11px] uppercase tracking-wider text-white/40">{t.stepAgree.tiersLabel}</p>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {tiers.map((tier, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-[#0D1015] p-3">
              <p className="text-[10px] uppercase tracking-wider text-white/45">{tier.label}</p>
              <p className="mt-1 text-2xl font-light text-[#D6A653]">{bpsToPct(tier.rate_bps)}</p>
              <p className="mt-1 text-[10px] text-white/45">
                {tier.max_seats == null
                  ? fmt(t.stepAgree.seatsPlus, { n: (tiers[i - 1]?.max_seats + 1) || 101 })
                  : fmt(t.stepAgree.seatsUpTo, { n: tier.max_seats })}
              </p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-[11px] leading-relaxed text-white/50">
          {attribText}
        </p>
      </div>

      <label className="mt-6 flex items-start gap-3 text-sm" data-testid="apply-agree-tos-row">
        <input
          type="checkbox"
          checked={tos}
          onChange={(e) => setTos(e.target.checked)}
          className="mt-1 h-4 w-4 accent-[#D6A653]"
          data-testid="apply-agree-tos"
        />
        <span className="text-white/75">
          {t.stepAgree.iAccept}{" "}
          <Link to="/legal/partner-terms" target="_blank" rel="noreferrer" className="text-[#D6A653] underline underline-offset-2" data-testid="apply-agree-tos-link">
            {t.stepAgree.termsLink}
          </Link>{" "}
          (v{config.tos_version}){t.stepAgree.clawbackNote}
        </span>
      </label>
    </div>
  );
};

const StepSuccess = ({ t, result, onDashboard }) => {
  const isPending = result?.partner?.status === "pending";
  const referral = useMemo(() => {
    if (isPending) return null;
    const code = result?.partner?.partner_code;
    if (!code) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return { code, link: `${origin}/api/partner/track/${code}` };
  }, [result, isPending]);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success(t.copyToast); }
    catch { toast.error(t.copyFailToast); }
  };

  const payoutEmail = result?.partner?.payout_email || "";

  return (
    <div data-testid="apply-step-success">
      <div className="inline-flex items-center gap-2 rounded-full border border-[#D6A653]/30 bg-[#0D1014] px-3 py-1">
        <CheckCircle2 className="h-3.5 w-3.5 text-[#F0CD84]" />
        <span className="text-[11px] text-[#E6C787]">{t.stepSuccess.submittedBadge}</span>
      </div>
      <h1 className="mt-5 text-3xl font-semibold tracking-tight sm:text-4xl" data-testid="apply-success-heading">
        {isPending ? t.stepSuccess.headingPending : t.stepSuccess.headingActive}
      </h1>
      <p className="mt-3 max-w-[600px] text-sm leading-relaxed text-white/60">
        {isPending ? (
          <>
            {t.stepSuccess.subPendingPrefix}
            <span className="text-white/85">{payoutEmail || t.stepSuccess.yourRegisteredEmail}</span>
            {t.stepSuccess.subPendingSuffix}
          </>
        ) : t.stepSuccess.subActive}
      </p>

      {referral && (
        <div className="mt-8 rounded-2xl border border-white/10 bg-[#0B0D12] p-5" data-testid="apply-success-referral">
          <p className="text-[11px] uppercase tracking-wider text-white/40">{t.stepSuccess.partnerCode}</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="rounded-md bg-[#0D1015] px-3 py-1.5 font-mono text-lg text-[#D6A653]" data-testid="apply-success-code">
              {referral.code}
            </code>
            <button onClick={() => copy(referral.code)} data-testid="apply-copy-code" className="rounded-md border border-white/12 p-2 text-white/60 hover:text-white">
              <Copy className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-4 text-[11px] uppercase tracking-wider text-white/40">{t.stepSuccess.referralLink}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md bg-[#0D1015] px-3 py-1.5 font-mono text-xs text-white/70" data-testid="apply-success-link">
              {referral.link}
            </code>
            <button onClick={() => copy(referral.link)} data-testid="apply-copy-link" className="rounded-md border border-white/12 p-2 text-white/60 hover:text-white">
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <Button onClick={onDashboard} data-testid="apply-success-dashboard" className="rounded-full bg-[#D6A653] px-6 py-3 text-sm font-medium text-black hover:bg-[#c99a49]">
          {t.stepSuccess.gotoDashboard} <ExternalLink className="ml-1.5 h-4 w-4" />
        </Button>
        <Link to="/partners" data-testid="apply-success-home" className="text-sm text-white/60 hover:text-white">
          {t.stepSuccess.backToProgram}
        </Link>
      </div>
    </div>
  );
};

// ---- Tiny helpers ---------------------------------------------------------

const Field = ({ label, hint, children }) => (
  <div className="w-full min-w-0 space-y-1.5">
    <Label className="text-[11px] uppercase tracking-wider text-white/50">{label}</Label>
    {children}
    {hint ? <p className="text-[11px] text-white/40">{hint}</p> : null}
  </div>
);

const Chips = ({ options, value, onChange, testIdPrefix }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => {
      const active = value === o.k;
      return (
        <button
          key={o.k}
          type="button"
          onClick={() => onChange(o.k)}
          data-testid={`${testIdPrefix}-${o.k}`}
          className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
            active
              ? "border-[#D6A653] bg-[#D6A653]/15 text-[#D6A653]"
              : "border-white/15 text-white/60 hover:text-white"
          }`}
        >
          {o.label}
        </button>
      );
    })}
  </div>
);

const SummaryRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3 border-t border-white/5 pt-2 first:border-none first:pt-0">
    <span className="text-[11px] uppercase tracking-wider text-white/40">{label}</span>
    <span className="truncate text-right text-sm text-white/85">{value || <span className="text-white/30">—</span>}</span>
  </div>
);
